import {
  buildInfoOwnerSummary,
  formatOwnerRequestCount,
} from '../../../../utils/informationManagerComponentUtils';

export { buildInfoOwnerSummary, formatOwnerRequestCount };

export const OWNER_TABLE_HEADERS = [
  { text: 'Owner', value: 'name', visible: true, filterable: true, sortable: true },
  { text: 'Total Request', value: 'totalRequest', visible: true, filterable: true, sortable: true },
  { text: 'Pending Request', value: 'pendingRequest', visible: true, filterable: true, sortable: true },
  { text: 'Last Followup Date', value: 'lastFollowupDate', visible: true, filterable: false, sortable: false },
  { text: 'Action', value: 'action', visible: true, filterable: false, sortable: false },
];

/** Higher weight: pending request count drives urgency (higher pending first). */
const PENDING_COUNT_WEIGHT = 100;
/** Medium weight: overall request volume. */
const TOTAL_REQUEST_WEIGHT = 10;
/** Lower weight: days since last follow-up. */
const FOLLOWUP_AGE_WEIGHT = 1;
const MAX_COUNT_FOR_NORM = 100;
const MAX_FOLLOWUP_AGE_DAYS = 365;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Priority score for owners that have at least one assigned request.
 * Pending count (high) > total request volume (medium) > follow-up age (low).
 * Higher pending count sorts earlier.
 * @param {Object} owner - Owner table row.
 * @param {number} [nowMs=Date.now()] - Reference timestamp for follow-up age.
 * @returns {number} Higher score sorts earlier.
 */
export function getOwnerWithRequestsPriorityScore(owner, nowMs = Date.now()) {
  const total = Number(owner?.totalRequest) || 0;
  const pending = Number(owner?.pendingRequest) || 0;
  const pendingNorm = Math.min(Math.max(pending, 0), MAX_COUNT_FOR_NORM) / MAX_COUNT_FOR_NORM;
  const totalNorm = Math.min(Math.max(total, 0), MAX_COUNT_FOR_NORM) / MAX_COUNT_FOR_NORM;

  const followUpMs = owner?.lastFollowupDate
    ? new Date(owner.lastFollowupDate).getTime()
    : NaN;
  const daysSinceFollowup = Number.isFinite(followUpMs)
    ? Math.max(0, (nowMs - followUpMs) / MS_PER_DAY)
    : MAX_FOLLOWUP_AGE_DAYS;
  const followUpNorm = Math.min(daysSinceFollowup, MAX_FOLLOWUP_AGE_DAYS) / MAX_FOLLOWUP_AGE_DAYS;

  return (
    (pendingNorm * PENDING_COUNT_WEIGHT)
    + (totalNorm * TOTAL_REQUEST_WEIGHT)
    + (followUpNorm * FOLLOWUP_AGE_WEIGHT)
  );
}

/**
 * Orders owner rows for the information owners table:
 * 1) Owners with totalRequest > 0, scored by pending count (high), total (medium), follow-up age (low).
 * 2) Owners with no requests, alphabetical by name.
 * @param {Array<Object>} rows - Owner table rows.
 * @returns {Array<Object>} Sorted copy of rows.
 */
export function sortOwnersForTable(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const nowMs = Date.now();
  const withRequests = [];
  const withoutRequests = [];

  rows.forEach((row) => {
    if ((Number(row?.totalRequest) || 0) > 0) {
      withRequests.push(row);
    } else {
      withoutRequests.push(row);
    }
  });

  withRequests.sort((a, b) => {
    const pendingDiff = (Number(b?.pendingRequest) || 0) - (Number(a?.pendingRequest) || 0);
    if (pendingDiff !== 0) return pendingDiff;

    const scoreDiff = getOwnerWithRequestsPriorityScore(b, nowMs)
      - getOwnerWithRequestsPriorityScore(a, nowMs);
    if (scoreDiff !== 0) return scoreDiff;
    return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
  });

  withoutRequests.sort((a, b) => (
    (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
  ));

  return [...withRequests, ...withoutRequests];
}

/**
 * Merges info owner records with aggregated request stats for the owners table.
 * @param {Array<Object>} infoOwners - Owner records from the store.
 * @param {Array<Object>} requests - Information request list.
 * @returns {Array<Object>} Owner table row view-models.
 */
export function buildOwnerTableRows(infoOwners = [], requests = []) {
  if (!Array.isArray(infoOwners)) return [];

  const summary = buildInfoOwnerSummary(requests);

  return infoOwners.map((owner) => {
    const stats = summary[owner._id] || {};
    const totalRequest = stats.totalRequest || 0;
    const pendingRequest = stats.pendingRequest || 0;
    const requestAssigned = stats.requestAssigned || 0;

    return {
      ...owner,
      totalRequest,
      requestAssigned,
      pendingRequest,
      lastFollowupDate: stats.lastmail || null,
      totalRequestLabel: formatOwnerRequestCount(totalRequest),
      requestAssignedLabel: formatOwnerRequestCount(requestAssigned),
      pendingRequestLabel: formatOwnerRequestCount(pendingRequest),
      status: owner.status !== false,
    };
  });
}

/**
 * Filters owner rows by a search query (name or email).
 * @param {Array<Object>} rows - Owner table rows.
 * @param {string} query - Search string.
 * @returns {Array<Object>} Filtered rows.
 */
export function filterOwnersBySearch(rows = [], query = '') {
  const term = (query || '').trim().toLowerCase();
  if (!term) return rows;

  return rows.filter((row) => {
    const name = (row.name || '').toLowerCase();
    const email = (row.email || '').toLowerCase();
    return name.includes(term) || email.includes(term);
  });
}
